package com.crowpay.views.components.nav

import com.crowpay.Resources
import com.crowpay.extensions.fullName
import com.crowpay.help2
import com.crowpay.help2Inverse
import com.crowpay.profile
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.sessionToken
import com.crowpay.selectedContractor
import com.crowpay.utils.Action
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.resize
import com.crowpay.utils.truncate
import com.crowpay.utils.withReverseIndex
import com.crowpay.views.dialogs.GenericDialog
import com.crowpay.views.dialogs.HelpDialog
import com.crowpay.views.dialogs.confirmationDialog
import com.crowpay.views.screens.auth.LogInScreen
import com.crowpay.views.screens.auth.UserProfile
import com.crowpay.views.screens.client.ClientDashboard
import com.crowpay.views.screens.contractor.ContractorBankInfo
import com.crowpay.views.screens.contractor.ContractorBillingInfo
import com.crowpay.views.screens.contractor.ContractorDashboard
import com.crowpay.views.screens.contractor.ContractorProfile
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.InitialIconSemantic
import com.crowpay.views.theming.RemoveOutline
import com.crowpay.views.theming.body
import com.crowpay.views.theming.boldSubTitle
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredContent
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.subTitle
import com.lightningkite.kiteui.Platform
import com.lightningkite.kiteui.current
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.ImageRemote
import com.lightningkite.kiteui.models.ImageScaleType
import com.lightningkite.kiteui.models.PopoverPreferredDirection
import com.lightningkite.kiteui.models.ThemeDerivation
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.AppState
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.closePopovers
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.icon
import com.lightningkite.kiteui.views.direct.image
import com.lightningkite.kiteui.views.direct.link
import com.lightningkite.kiteui.views.direct.menuButton
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.text
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.l2.icon


fun ViewWriter.crowpayNavHeaderWide(): ViewModifiable =
    col {
        spacing = 0.dp
        existsDefaultFalse { navExists() }

        padded - row {
            spacing = 1.5.rem

            buttonTheme - button {
                sizeConstraints(height = 2.rem, width = 9.567.rem) - centered - image {
                    source = Resources.imagesFullLogoColor
                    scaleType = ImageScaleType.Fit
                }
                onClick("Navigate Home") {
                    mainScreenNavigator.navigate(
                        if (selectedContractor() == null) ClientDashboard() else ContractorDashboard()
                    )
                }
            }

            expanding - space()

            row {
                spacing = 0.5.rem

                buttonTheme - centered - helpButton()

                buttonTheme - centered - UserNotifications.navButton(this)

                buttonTheme - centered - userButton()
            }
        }

        sizeConstraints(height = 2.dp) - stack {
            themeChoice += ThemeDerivation { it.copy(id = "grnline", background = AppColors.secondary.main).withBackNoPadding }
        }
    }

interface Form {
    val saved: Readable<Boolean>
}

fun ViewWriter.crowpayNavHeaderNarrow(): ViewModifiable =
    row {
        existsDefaultFalse { navExists() }

        expanding - row {
            spacing = 5.dp
            centered - onlyWhen { mainScreenNavigator.canGoBack() } - buttonTheme - button {
                icon(Icon.arrowBack.resize(1.25.rem), "arrow back")
                onClick("Go Back") {
                    val current = mainScreenNavigator.currentScreen()
                    if (current is Form && !current.saved()) confirmationDialog(
                        "Discard Changes",
                        "This page has unsaved changes, exiting will lose this information. Are you sure you want to do this?",
                        "Discard and Exit"
                    ) { mainScreenNavigator.goBack() }
                    else mainScreenNavigator.goBack()
                }
            }

            expanding - centered - boldSubTitle {
                wraps = false
                ellipsis = true
                ::content { mainScreenNavigator.currentScreen()?.title?.invoke()?.truncate(20) ?: "" }
            }
        }

        row {
            spacing = 0.5.rem

            buttonTheme - centered - helpButton()

            buttonTheme - centered - userButton()
        }
    }


private fun ViewWriter.helpButton() = menuButton {
    requireClick = true
    icon {
        ::source {
            if (windowIsNarrow()) Icon.help2.resize(1.3.rem)
            else Icon.help2Inverse.resize(2.rem)
        }
        description = "Help"
    }

    opensMenu {
        stack {
            RemoveOutline.onNext - col {
                spacing = 0.rem

                val options = listOf(
                    Action("Contact CrowPay") { dialogScreenNavigator.navigate(HelpDialog()) },
                    Action("Support Library") { dialogScreenNavigator.navigate(GenericDialog("Coming Soon")) }
                )

                for ((idx, option) in options.withReverseIndex()) {
                    compact - button {
                        specCenteredText(option.title)
                        action = option
                    }
                    if (idx != 0) greySeparator()
                }
            }
        }
    }
}

private fun ViewWriter.userButton() = menuButton {
    preferredDirection = PopoverPreferredDirection.belowLeft
    requireClick = true

    val membership = shared {
        val session = currentSession() ?: return@shared null
        val ship = session.self().membership ?: return@shared null
        session.contractors[ship.contractor]()
    }

    row {
        sizeConstraints(height = 2.rem, width = 2.rem) - centered - image {
            existsDefaultFalse {
                membership() != null && membership()?.image?.location?.isNotBlank() == true
            }
            ::source {
                membership()?.image
                    ?.location
                    ?.takeUnless { it.isBlank() }
                    ?.let(::ImageRemote)
                    ?: Icon.Companion.profile.toImageSource(AppColors.primary.main)
            }
            description = "Switch Profile"
        }

        centered - sizeConstraints(aspectRatio = 1.0) - InitialIconSemantic.onNext - stack {
            existsDefaultFalse {
                membership() == null || membership()?.image?.location.isNullOrBlank()
            }
            centered - text {
                align = Align.Center
                ::content {
                    currentSession.awaitNotNull().self()
                        .let { "${it.firstName.take(1)}${it.lastName.take(1)}" }
                }
            }
        }

        centered - subTitle {
            align = Align.Center
            ::content {
                val isNarrow = windowIsNarrow()

                membership()?.name?.take(if (isNarrow) 10 else 20)
                    ?: if (isNarrow) "" else currentSession()?.self?.invoke()?.fullName ?: ""
            }
        }
    }

    opensMenu {
        sizeConstraints(minWidth = AppDimensions.navMenuWidth) - col {
            compact - col {
                subTitle {
                    align = Align.Center
                    ::content { currentSession()?.self?.invoke()?.fullName ?: "Not Logged In" }
                }
                smallBody {
                    existsDefaultFalse { membership() != null }
                    align = Align.Center
                    ::content { membership()?.name ?: "" }
                }
            }
            RemoveOutline.onNext - col {
                spacing = 0.dp
                fun ViewWriter.navlink(name: String, to: () -> Page) {
                    buttonTheme - link {
                        body(name)
                        onNavigate { closePopovers() }
                        this.to = to
                    }
                    greySeparator()
                }

                navlink("User Profile") { UserProfile() }
                col {
                    spacing = 0.dp
                    existsDefaultFalse { membership() != null }
                    navlink("Business Info") { ContractorProfile() }
                    navlink("Bank Info") { ContractorBankInfo() }
                    navlink("Billing") { ContractorBillingInfo() }
                }
                button {
                    specCenteredContent {
                        centered - icon(Icon.logout, "log out")
                        centered - body("Log Out")
                    }
                    onClick {
                        closePopovers()
                        confirmationDialog(
                            "Log Out?",
                            "Are you sure you want to log out?",
                            "Log Out"
                        ) {
                            try {
                                currentSession()?.nonCached?.userAuth?.terminateSession()
                            } catch (_: Exception) {
                            }
                            sessionToken.set(null)
                            screenNavigator.reset(LogInScreen())
                        }
                    }
                }
            }
        }
    }
}